import { isDefined } from "@clipboard-health/util-ts";
import { Paper, Stack } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { type WorkplaceCommentModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { Text, type UseModalState, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useGetComment } from "@src/appV2/redesign/Workplace/Review/api/useGetComment";
import {
  ReviewSource,
  useWorkplaceComments,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { useParams } from "react-router-dom";

import { Reaction } from "../types";
import { isViewerAuthor } from "../utils";
import { WorkplaceReviewCommentCard } from "./Card";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";
import { WorkplaceReviewCommentLikesButton } from "./LikesButton";
import { WorkplaceCommentPostReplyFooter } from "./PostReplyFooter";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { WorkplaceReviewCommentRepliesCountIcon } from "./RepliesCountIcon";
import { ReviewContextPills } from "./ReviewContextPills";
import { useUpdateCachedComment } from "./useUpdateCachedComment";
import { VideoCommentPlayer } from "./VideoCommentPlayer";
import { WorkplaceReviewRemoveIcon } from "./WorkplaceReviewRemoveIcon";

interface WorkplaceCommentModalProps {
  modalState: UseModalState;
  workplaceId: string;
}

export function WorkplaceCommentModal(props: WorkplaceCommentModalProps) {
  const { modalState, workplaceId } = props;

  const { commentId } = useParams<WorkplaceCommentModalRoutePathParams>();
  const worker = useDefinedWorker();

  const { useDelayedReviewUI } = useCbhFlag(CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW],
  });

  // TODO: This modal needs to be split into a container component that handles loading states
  // and a presentational component that receives the destructured comment data
  const { data: commentData, isSuccess: isCommentSuccess } = useGetComment({
    workplaceId,
    commentId,
  });

  const commentsParams = {
    workplaceId,
    filter: {
      parentCommentId: commentId,
    },
  };

  const source = commentData?.data.attributes.source;
  const externalDetails = commentData?.data.attributes.externalDetails;
  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(commentData?.data.attributes.workerInfo.id), {
      enabled: isDefined(commentData?.data.attributes.workerInfo.id) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  let displayDate = new Date();
  if (isExternalReview && isDefined(externalDetails?.publishedAt)) {
    displayDate = parseISO(externalDetails.publishedAt);
  } else if (isDefined(commentData)) {
    displayDate = parseISO(commentData.data.attributes.createdAt);
  }

  const { data: replies, isSuccess: isRepliesSuccess } = useWorkplaceComments(commentsParams);

  const sortedReplies =
    replies?.toSorted((a, b) => {
      return (
        new Date(b.attributes.createdAt).getTime() - new Date(a.attributes.createdAt).getTime()
      );
    }) ?? [];

  const { updateComment, deleteComment } = useUpdateCachedComment({
    workplaceId,
    commentsParams,
  });

  const removeModalState = useModalState();
  const viewerIsAuthor =
    isDefined(commentData) &&
    isViewerAuthor({
      workerInfo: commentData.data.attributes.workerInfo,
      viewerUserId: worker.userId,
    });

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <PageHeaderTopRow />

      <Stack sx={{ overflow: "hidden", height: "100%" }}>
        <Paper variant="tertiary" sx={{ px: 7, paddingBottom: 8 }}>
          {isCommentSuccess &&
            (useDelayedReviewUI ? (
              <Stack spacing={4}>
                <WorkplaceReviewCommentAuthorInfo
                  useDelayedReviewLayout
                  workerName={commentData.data.attributes.workerInfo.name}
                  profilePictureUrl={profilePictureUrl}
                  isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
                  createdAt={displayDate}
                  shiftWorkerQualification={commentData.data.attributes.shiftWorkerQualification}
                  reviewUrl={externalDetails?.reviewUrl}
                />

                <ReviewContextPills
                  shiftPeriod={commentData.data.attributes.shiftPeriod}
                  patientsPerNurse={commentData.data.attributes.patientsPerNurse}
                  reviewTag={commentData.data.attributes.reviewTag}
                  rating={commentData.data.attributes.rating}
                  source={source}
                />

                {commentData.data.attributes.type === "VIDEO" &&
                isDefined(commentData.data.attributes.media?.[0]?.url) ? (
                  <VideoCommentPlayer
                    url={commentData.data.attributes.media[0].url}
                    workplaceId={workplaceId}
                    commentId={commentId}
                  />
                ) : (
                  <Text semibold variant="h6">
                    {commentData.data.attributes.text}
                  </Text>
                )}

                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                  <WorkplaceReviewCommentRepliesAndLikesWrapper>
                    <WorkplaceReviewCommentRepliesCountIcon
                      count={commentData.data.attributes.aggregates.totalReplies}
                    />
                    <WorkplaceReviewCommentLikesButton
                      workplaceId={workplaceId}
                      commentId={commentId}
                      isLiked={commentData.data.attributes.workerReaction === Reaction.LIKE}
                      count={commentData.data.attributes.aggregates.totalLikes}
                      onLikeChanged={(newComment) => {
                        updateComment(newComment);
                      }}
                    />
                    {viewerIsAuthor && !isExternalReview && (
                      <WorkplaceReviewRemoveIcon
                        onRemove={() => {
                          removeModalState.openModal();
                        }}
                      />
                    )}
                  </WorkplaceReviewCommentRepliesAndLikesWrapper>
                </Stack>
              </Stack>
            ) : (
              <>
                {commentData.data.attributes.type === "VIDEO" &&
                isDefined(commentData.data.attributes.media?.[0]?.url) ? (
                  <VideoCommentPlayer
                    url={commentData.data.attributes.media[0].url}
                    workplaceId={workplaceId}
                    commentId={commentId}
                  />
                ) : (
                  <Text semibold variant="h6" sx={{ paddingBottom: 8 }}>
                    {commentData.data.attributes.text}
                  </Text>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                  <WorkplaceReviewCommentAuthorInfo
                    workerName={commentData.data.attributes.workerInfo.name}
                    profilePictureUrl={profilePictureUrl}
                    isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
                    createdAt={displayDate}
                    reviewUrl={externalDetails?.reviewUrl}
                  />

                  <WorkplaceReviewCommentRepliesAndLikesWrapper>
                    <WorkplaceReviewCommentRepliesCountIcon
                      count={commentData.data.attributes.aggregates.totalReplies}
                    />
                    <WorkplaceReviewCommentLikesButton
                      workplaceId={workplaceId}
                      commentId={commentId}
                      isLiked={commentData.data.attributes.workerReaction === Reaction.LIKE}
                      count={commentData.data.attributes.aggregates.totalLikes}
                      onLikeChanged={(newComment) => {
                        updateComment(newComment);
                      }}
                    />
                    {viewerIsAuthor && !isExternalReview && (
                      <WorkplaceReviewRemoveIcon
                        onRemove={() => {
                          removeModalState.openModal();
                        }}
                      />
                    )}
                  </WorkplaceReviewCommentRepliesAndLikesWrapper>
                </Stack>
              </>
            ))}
        </Paper>

        <Paper variant="secondary" sx={{ px: 5, py: 7, flexGrow: 1, overflow: "auto" }}>
          {isRepliesSuccess && (
            <Stack spacing={5}>
              {sortedReplies.length === 0 && (
                <Text variant="h6" sx={{ textAlign: "center" }}>
                  No replies yet. Be the first to reply!
                </Text>
              )}

              {sortedReplies.map((reply) => (
                <WorkplaceReviewCommentCard
                  key={reply.id}
                  workplaceId={workplaceId}
                  comment={reply}
                  viewerIsAuthor={isViewerAuthor({
                    workerInfo: reply.attributes.workerInfo,
                    viewerUserId: worker.userId,
                  })}
                  onLikeChanged={(newComment) => {
                    updateComment(newComment);
                  }}
                  onAnonymized={(newComment) => {
                    updateComment(newComment);
                  }}
                  onRemoved={(commentId) => {
                    deleteComment(commentId);
                  }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>

      <WorkplaceCommentPostReplyFooter workplaceId={workplaceId} parentId={String(commentId)} />
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={commentData.data.id}
          workplaceId={workplaceId}
          onRemoved={(commentId) => {
            deleteComment(commentId);
            modalState.closeModal();
          }}
        />
      )}
    </FullScreenDialog>
  );
}
